/* Cœura engraving model + customer composer (v3, 9/13: styles per face, moon-sign glyphs, plain monograms).
   MODEL: carigram-config.json (code defaults) merged with engraving-params.json (what Patti publishes from the
   Engraving Bench). One product:
     product   = { handle, form, blank, choice:'one'|'any', placements:[placement] }
     placement = { id, label, sub, side, required, plates:{metal:{src,w,h}}, boxes:{metal|all:{x,y,w,h,rotate}},
                   options:[style] }                       the compositions this face offers the customer
     style     = { id, label, mode:'engraving'|'initials'|'date'|'monogram',
                   text:{ lines:[{kind,limit,label,font}], fonts, orientation, slider, maxPct, fieldMm, lh, noRotate },
                   monogram:{ count, font, tracking, separator, rule }, boxes? }
   A placement with no options behaves as one style (its own mode/text), which is how every product built before
   9/13 still works. PRESETS below are the option library from Patti's sheets: any product can use any of them.
   COMPOSER: builds "Compose your piece", drives the stage, gates the CTA, writes line-item properties and the
   add-to-cart proof. Used by pdp.js, begin.js and the Engraving Bench's try-it pane. */
(function () {
  'use strict';
  function R() { return window.CarigramRender; }
  var METAL_TOKEN = { 'Sterling Silver': 'silver', '18K Gold Vermeil': 'vermeil', 'Mixed Metals': 'mixed' };
  var METAL_LABEL = { silver: 'Sterling Silver', vermeil: '18K Gold Vermeil', mixed: 'Mixed Metals', 'default': 'Every metal' };
  var MODE_WAIT = { initials: 'Your letters first', monogram: 'Your initials first', date: 'Your date first', engraving: 'Your words first' };
  var SIDE_COPY = { front: 'engraved on the face, worn outward.', back: 'engraved on the back, where only you read it.', inside: 'engraved inside, where only you read it.', left: 'engraved on the left.', right: 'engraved on the right.' };

  /* the twelve moon signs (BACK A on the Moon collection sheet) */
  var GLYPHS = [
    { id: 'aries', label: 'Aries', char: '♈' }, { id: 'taurus', label: 'Taurus', char: '♉' },
    { id: 'gemini', label: 'Gemini', char: '♊' }, { id: 'cancer', label: 'Cancer', char: '♋' },
    { id: 'leo', label: 'Leo', char: '♌' }, { id: 'virgo', label: 'Virgo', char: '♍' },
    { id: 'libra', label: 'Libra', char: '♎' }, { id: 'scorpio', label: 'Scorpio', char: '♏' },
    { id: 'sagittarius', label: 'Sagittarius', char: '♐' }, { id: 'capricorn', label: 'Capricorn', char: '♑' },
    { id: 'aquarius', label: 'Aquarius', char: '♒' }, { id: 'pisces', label: 'Pisces', char: '♓' }
  ];
  /* a moon sign only ever belongs on a glyph line: keep the characters out of typed lines, where they would
     fall back to the emoji face */
  var GLYPH_RE = /[♈-♓︎️]/g;
  function stripGlyphs(v) { return String(v == null ? '' : v).replace(GLYPH_RE, ''); }
  /* A "Coordinates" composition: a Location field fills its two lines. Recognised by the preset
     Patti picked in the bench, or by the line labels if the entry was seeded some other way. */
  function isCoords(v) {
    if (!v || !v.text) return false;
    if (v.preset === 'coordinates') return true;
    var lab = v.text.lines.map(function (l) { return String(l.label || '').toLowerCase(); });
    return lab.indexOf('latitude') >= 0 && lab.indexOf('longitude') >= 0;
  }
  function coordLines(t) {
    var lat = -1, lng = -1;
    t.lines.forEach(function (l, k) {
      var lab = String(l.label || '').toLowerCase();
      if (lab === 'latitude') lat = k; else if (lab === 'longitude') lng = k;
    });
    if (lat < 0 || lng < 0) {                        // unlabelled: first two typed lines, in order
      var typed = [];
      t.lines.forEach(function (l, k) { if (l.kind !== 'glyph') typed.push(k); });
      lat = typed[0]; lng = typed[1];
    }
    return { lat: lat, lng: lng };
  }
  /* index of the moon-sign line, or -1 */
  function glyphLine(t) {
    if (!t) return -1;
    for (var k = 0; k < t.lines.length; k++) if (t.lines[k].kind === 'glyph') return k;
    return -1;
  }

  function glyphBy(v) {
    for (var i = 0; i < GLYPHS.length; i++) if (GLYPHS[i].id === v || GLYPHS[i].char === v) return GLYPHS[i];
    return null;
  }

  var BLANK_GROUND = '<rect x="0" y="0" width="100" height="100" fill="#EDE6D9"/>';   // a face set to a plain ground
  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function clone(o) { return o == null ? o : JSON.parse(JSON.stringify(o)); }
  function chars(s) { return Array.from(String(s || '')); }
  function clen(s) { return chars(s).length; }
  function num(v, d) { var x = +v; return isFinite(x) ? x : d; }

  /* ------------------------------------------------------------------ model */
  var TONE_LABEL = { silver: 'Sterling silver', vermeil: '18K gold vermeil', mixed: 'Two-tone, silver and gold', 'mixed-gold': 'Two-tone, gold and silver' };
  var TEXT_DEFAULTS = { lines: [{ kind: 'text', limit: 14, label: '', font: null }], maxTotal: null, fonts: ['script', 'caps'], caps: [], paragraph: false, matchSmallest: false, orientation: 'h', slider: true, maxPct: 100, fieldMm: null, lh: 1.25, noRotate: false };
  var MONO_DEFAULTS = { count: 2, font: 'serif', tracking: 0.5, separator: true, rule: true, maxPct: 100 };
  function fillText(t) {
    t = t || {};
    var o = clone(TEXT_DEFAULTS);
    Object.keys(t).forEach(function (k) { if (t[k] !== undefined) o[k] = clone(t[k]); });
    if (!Array.isArray(o.lines) || !o.lines.length) o.lines = clone(TEXT_DEFAULTS.lines);
    o.lines = o.lines.map(function (l) {
      var kind = l && l.kind === 'glyph' ? 'glyph' : 'text';
      return {
        kind: kind,
        limit: kind === 'glyph' ? 1 : Math.max(1, Math.round(num(l && l.limit, 14))),
        label: (l && l.label) || '',
        font: kind === 'glyph' ? 'zodiac' : ((l && l.font) || null),
        /* this line's own largest size, as a percent of the area height; null follows the composition */
        maxPct: (l && l.maxPct != null && isFinite(+l.maxPct)) ? Math.max(20, Math.min(100, Math.round(+l.maxPct))) : null
      };
    });
    if (!Array.isArray(o.fonts) || !o.fonts.length) o.fonts = ['script'];
    if (!Array.isArray(o.caps)) o.caps = o.caps === true ? o.fonts.slice() : [];
    o.noRotate = !!o.noRotate;
    o.maxTotal = null;   // no separate cap: the total is always the line limits added up (16 x 3 lines = 48)
    return o;
  }
  function fillMono(m) {
    m = m || {};
    var o = clone(MONO_DEFAULTS);
    Object.keys(m).forEach(function (k) { if (m[k] !== undefined) o[k] = m[k]; });
    o.count = o.count === 3 ? 3 : 2;
    return o;
  }

  /* ---- the option library from Patti's sheets: available to every product ---- */
  function L(limit, label, font, kind) { return { kind: kind || 'text', limit: limit, label: label || '', font: font || null }; }
  var PRESETS = [
    { id: 'lines', label: 'Lines, horizontal', group: 'Lines', mode: 'engraving', text: { lines: [L(16), L(16), L(16)], fonts: ['script', 'serif'], orientation: 'h' } },
    { id: 'linesv', label: 'Lines, vertical', group: 'Lines', mode: 'engraving', text: { lines: [L(16), L(16), L(16)], fonts: ['script', 'serif'], orientation: 'up' } },
    /* legacy: the per-count ids stay resolvable for entries published before 9/14, but the bench offers one
       Lines type and the number of lines is set in Lettering */
    { id: 'line1', label: 'One line', group: 'Lines', legacy: true, mode: 'engraving', text: { lines: [L(16)], fonts: ['script', 'serif'] } },
    { id: 'line2', label: 'Two lines', group: 'Lines', legacy: true, mode: 'engraving', text: { lines: [L(16), L(16)], fonts: ['script', 'serif'] } },
    { id: 'line3', label: 'Three lines', group: 'Lines', legacy: true, mode: 'engraving', text: { lines: [L(16), L(16), L(16)], fonts: ['script', 'serif'] } },
    { id: 'line5', label: 'Up to five lines', group: 'Lines', legacy: true, mode: 'engraving', text: { lines: [L(16), L(16), L(16), L(16), L(16)], fonts: ['script', 'serif'] } },
    { id: 'coordinates', label: 'Coordinates', group: 'Sets', mode: 'engraving', text: { lines: [L(12, 'Latitude', 'pitch'), L(12, 'Longitude', 'pitch')], fonts: ['pitch'] } },
    { id: 'date', label: 'Date, stacked', group: 'Sets', mode: 'engraving', text: { lines: [L(2, 'Day', 'pitch'), L(2, 'Month', 'pitch'), L(2, 'Year', 'pitch')], fonts: ['pitch'], noRotate: true, slider: false } },
    { id: 'placedate', label: 'Place, date, sentiment', group: 'Sets', mode: 'engraving', text: { lines: [L(18, 'Place', 'pitch'), L(18, 'Date', 'serif'), L(18, 'Sentiment', 'pitch')], fonts: ['pitch', 'serif'] } },
    { id: 'namesign', label: 'Name and moon sign', group: 'Sets', mode: 'engraving', text: { lines: [L(12, 'Name', 'serif'), L(1, 'Moon sign', 'zodiac', 'glyph')], fonts: ['serif'] } },
    { id: 'upload', label: 'Your own artwork', group: 'Upload', mode: 'upload', faceOnly: true, upload: { note: '' } },
    { id: 'moonsign', label: 'Moon sign', group: 'Symbols', mode: 'engraving', text: { lines: [L(1, 'Moon sign', 'zodiac', 'glyph')], fonts: ['serif'] } },
    { id: 'mono2', label: 'Initials, two', group: 'Initials', mode: 'monogram', monogram: { count: 2 } },
    { id: 'mono3', label: 'Initials, three', group: 'Initials', mode: 'monogram', monogram: { count: 3 } },
    { id: 'interlaced', label: 'Interlaced initials', group: 'Initials', mode: 'initials' },
    { id: 'moonphase', label: 'Moon phase', group: 'Moon', mode: 'date' }
  ];
  /* every lines layout, across counts and directions; which of the two types it ticks in the bench is
     decided by its direction, so a composition published before 9/14 lands in the right box */
  var LINES_FAMILY = ['lines', 'linesv', 'line1', 'line2', 'line3', 'line5'];
  /* the Lines family: the only compositions where matching every line to one size makes sense
     (Coordinates, a stacked date or Place/date/sentiment each want their own line sizes) */
  function isLines(v) { return !!v && LINES_FAMILY.indexOf(v.preset) !== -1; }
  function presetById(id) { for (var i = 0; i < PRESETS.length; i++) if (PRESETS[i].id === id) return PRESETS[i]; return null; }
  /* apply a preset to a style, keeping its own name and box */
  function applyPreset(style, id) {
    var p = presetById(id);
    if (!p) return style;
    style = style || {};
    style.preset = p.id;
    style.mode = p.mode;
    if (p.mode === 'engraving') { style.text = fillText(clone(p.text)); delete style.monogram; }
    else if (p.mode === 'monogram') { style.monogram = fillMono(clone(p.monogram)); delete style.text; }
    else if (p.mode === 'upload') { style.upload = clone(p.upload) || { note: '' }; delete style.text; delete style.monogram; }
    else { delete style.text; delete style.monogram; }
    return style;
  }
  function fillStyle(s, j) {
    s = clone(s) || {};
    s.id = s.id || ('s' + (j + 1));
    s.mode = s.mode || 'engraving';
    if (s.mode === 'engraving') s.text = fillText(s.text);
    if (s.mode === 'monogram') s.monogram = fillMono(s.monogram);
    if (s.mode === 'upload') s.upload = s.upload || { note: '' };
    return s;
  }
  /* the compositions a face offers; a placement without options is itself the only one */
  function stylesOf(pl) { return (pl && pl.options && pl.options.length) ? pl.options : [pl]; }
  /* one style as a placement-like object the renderer can draw */
  function styleView(pl, j) {
    var list = stylesOf(pl), s = list[j] || list[0];
    if (s === pl) return pl;
    return {
      /* a composition with no name of its own falls back to its layout's name, never to the face's */
      id: s.id || pl.id, label: s.label || '', sub: s.sub || pl.sub, side: pl.side, preset: s.preset, tone: pl.tones || pl.tone || '',
      mode: s.mode || pl.mode, text: s.text, monogram: s.monogram, upload: s.upload,
      plates: pl.plates, boxes: (s.boxes && Object.keys(s.boxes).length) ? s.boxes : pl.boxes
    };
  }

  function boxOnly(b) { b = b || {}; return { x: num(b.x, 30), y: num(b.y, 30), w: num(b.w, 40), h: num(b.h, 40), rotate: num(b.rotate, 0) }; }
  function legacyText(box, cfg) {
    var max = (box && box.maxChars) || cfg.engraveMax || 24;
    if (cfg.singleLine) return fillText({ lines: [L(max)] });
    var per = Math.ceil(max / 2);
    return fillText({ lines: [L(per), L(per)] });
  }
  function legacyPlates(cfg, placementId) {
    var out = {}, set = cfg.plates && ((placementId && cfg.plates[placementId]) || cfg.plates['default']);
    if (set) { Object.keys(set).forEach(function (m) { out[m] = { src: set[m], w: 1200, h: 1200, legacy: true }; }); return out; }
    if (cfg.plate) ['silver', 'vermeil'].forEach(function (m) { out[m] = { src: 'plate-' + cfg.form + '-' + m + '.jpg', w: 1024, h: 1024, legacy: true }; });
    return out;
  }
  /* carigram-config.json entry -> v2 product */
  function normalize(handle, cfg) {
    if (!cfg) return null;
    var mode = (cfg.modes && cfg.modes[0]) || 'engraving', placements;
    if (cfg.placements && cfg.placements.length) {
      placements = cfg.placements.map(function (p) {
        var b = p.engraveBox || cfg.engraveBox;
        var pl = { id: p.id, label: p.label || '', sub: p.sub || '', side: p.side || '', mode: mode, required: false, plates: legacyPlates(cfg, p.id), boxes: { all: boxOnly(b) } };
        if (mode === 'engraving') pl.text = legacyText(b, cfg);
        return pl;
      });
    } else {
      var boxes = { all: boxOnly(cfg.engraveBox) };
      if (cfg.engraveBoxByMetal) Object.keys(cfg.engraveBoxByMetal).forEach(function (m) { boxes[m] = boxOnly(cfg.engraveBoxByMetal[m]); });
      var one = { id: 'main', label: '', sub: '', side: '', mode: mode, required: true, plates: legacyPlates(cfg, null), boxes: boxes };
      if (mode === 'engraving') one.text = legacyText(cfg.engraveBox, cfg);
      placements = [one];
    }
    /* several faces means the customer can engrave any of them; a single face is simply that one */
    return { handle: handle, form: cfg.form, type: cfg.type, blank: !!cfg.blank, choice: placements.length > 1 ? 'any' : 'one', placements: placements, source: 'config' };
  }
  /* an Engraving Bench entry replaces the config's placements */
  function merge(norm, entry, handle) {
    if (!entry || !Array.isArray(entry.placements)) return norm;
    var out = norm ? clone(norm) : { handle: handle, form: entry.form || handle, type: '', blank: false, choice: 'one', placements: [] };
    out.placements = entry.placements.map(function (pl, i) {
      var p = clone(pl);
      p.id = p.id || ('p' + (i + 1));
      p.plates = p.plates || {};
      p.boxes = p.boxes && Object.keys(p.boxes).length ? p.boxes : { all: boxOnly(null) };
      if (p.options && p.options.length) p.options = p.options.map(fillStyle);
      else {
        p.mode = p.mode || 'engraving';
        if (p.mode === 'engraving') p.text = fillText(p.text);
        if (p.mode === 'monogram') p.monogram = fillMono(p.monogram);
      }
      return p;
    });
    out.choice = out.placements.length > 1 ? 'any' : 'one';
    if (entry.form) out.form = entry.form;
    out.blank = !!entry.blank;
    out.source = 'bench';
    /* A moon face the bench gave no photo to. The bench's "No photo" means "show the product shot,
       no preview", which is a real choice for an ENGRAVING face — but a date face with no plate
       can't draw a moon at all, so there is nothing to override. Fall back to what the config
       already knows: its plate AND its box together, since the box is measured against that plate
       and the two are meaningless apart. */
    if (norm && norm.placements && norm.placements.length) {
      out.placements.forEach(function (pl) {
        if (pl.plates && Object.keys(pl.plates).length) return;
        var isDate = pl.mode === 'date' || (pl.options || []).some(function (o) { return o.mode === 'date'; });
        if (!isDate) return;
        var from = null;
        for (var i = 0; i < norm.placements.length; i++) {
          var n = norm.placements[i];
          if (!n.plates || !Object.keys(n.plates).length) continue;
          if (n.mode !== 'date') continue;
          if (!from || n.id === pl.id) from = n;
        }
        if (!from) return;
        pl.plates = clone(from.plates);
        pl.boxes = clone(from.boxes);
      });
    }
    /* The bench decides which faces a piece has, including whether it has a moon face at all: a date piece
       whose bench entry carries no moon-phase face simply has none. Nothing is added back from the config
       here — a face the bench cannot see is a face Patti cannot remove, and every edit after it lands on
       the wrong face. To put the moon back, tick the face in the bench and give it the Moon phase
       composition; the block above hands it the config's disc and box. */
    return out;
  }
  function productFor(handle, data) {
    data = data || {};
    var norm = normalize(handle, data.config && data.config[handle]);
    var entry = data.params && data.params.products && data.params.products[handle];
    return merge(norm, entry, handle);
  }
  function fetchJSON(u) {
    if (!u) return Promise.resolve(null);
    return fetch(u, { cache: 'reload' }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  }
  function load(configUrl, paramsUrl) {
    return Promise.all([fetchJSON(configUrl), fetchJSON(paramsUrl)]).then(function (a) { return { config: a[0] || {}, params: a[1] }; });
  }
  /* which plate a metal shows (a Mixed Metals piece shows the silver plate unless it has its own) */
  function plateKey(pl, metal) {
    var p = (pl && pl.plates) || {};
    if (metal && p[metal]) return metal;
    if (metal === 'mixed' && p.silver) return 'silver';
    if (p['default']) return 'default';
    if (p.silver) return 'silver';
    if (p.vermeil) return 'vermeil';
    return Object.keys(p)[0] || null;
  }
  function resolveSrc(src, assetBase) {
    if (!src) return '';
    if (/^(https?:)?\/\//.test(src)) return src;
    return (assetBase || '') + src;
  }
  /* kind: 'plate' (photo + overlay), 'blank' (charm: letters on bone), 'none' (product photo, no overlay) */
  /* the night this face is set to, if it is a date face and the customer has named one */
  function nightOf(pl, state) {
    if (!state || !state.night) return null;
    var isDate = pl.mode === 'date' || (pl.options || []).some(function (o) { return o.mode === 'date'; });
    return isDate ? state.night : null;
  }

  function stageFor(prod, pl, metal, assetBase, night) {
    /* A night John has already rendered replaces the product shot outright — that image IS the
       piece on that night, bezel, bail and all — and carigram-render draws no moon over it. Pieces
       and metals with no rendered set fall through to the plate below and are composited as before. */
    if (night && R() && R().hasMoonFrames && R().hasMoonFrames(prod.handle, metal) && window.COMoon) {
      var when = R().nightDate(night);
      var fr = when && R().moonFrame(prod.handle, metal, window.COMoon.age(when));
      if (fr) return { kind: 'plate', key: metal, src: resolveSrc(fr.src, assetBase), vh: fr.vh, frame: fr.day };
    }
    var k = plateKey(pl, metal), p = k && pl.plates[k];
    if (p && p.blank) return { kind: 'blank', key: k, src: '', vh: 100 };
    if (p) return { kind: 'plate', key: k, src: resolveSrc(p.src, assetBase), vh: 100 * (+p.h || 1) / (+p.w || 1) };
    // A personalization face with no plate photo (e.g. the Moon pendant's "back", or a piece before its
    // plate is published) composes on a bone ground so the customer still sees their engraving/sign,
    // rather than a bare product photo with nothing on it. Once a plate (or a plate:{blank:true}) is
    // published for the face it becomes 'plate'/'blank' above.
    return { kind: 'blank', key: null, src: '', vh: 100 };
  }

  /* ------------------------------------------------------------------ composer */
  /* o: { ui, stage, overlay, product, assetBase, getMetal, getVariant, onChange, onShowBase, api,
          meta:{handle,productId,title}, heading } */
  function Composer(o) {
    var prod = o.product, P = prod.placements, ui = o.ui;
    /* A piece with more than one face opens with NONE selected: the customer clicks Front / Back / Inside
       (etc.) and only then does the stage swap to that face's photo. A single-face piece has nothing to
       choose, so it stays on its one face. */
    var st = { active: P.length > 1 ? -1 : 0, per: P.map(function (pl) { return { style: 0, s: stylesOf(pl).map(function (_, j) { return blankState(styleView(pl, j)); }) }; }) };
    var lays = P.map(function (pl) { return stylesOf(pl).map(function () { return null; }); });
    /* every moon-sign date field on the piece, so the moon face's date can autofill the sign faces (the
       moon sign is read from the same night). Each holds its own "was this auto-filled?" flag so a date
       the customer typed themselves is never overwritten. Rebuilt each wire(). */
    var skyCtls = [];
    var dead = false;

    function blankState(v) {
      var n = v.text ? v.text.lines.length : 0, a = [];
      for (var i = 0; i < n; i++) a.push({ text: '', t: 1 });
      return { letters: '', night: '', lines: a, font: v.text ? v.text.fonts[0] : 'script', shown: 1,
        art: '', artName: '', artUrl: '',
        autoDate: -1,         // the line holding a date the moon face wrote in (see wire), -1 for none
        place: null,          // the place the coordinates were filled from, for the order record
        sky: { place: null, date: '', time: '' },   // a moon sign is read from these three
        match: isLines(v) && !!(v.text && v.text.matchSmallest) };
    }
    function sv(i, j) { return styleView(P[i], j == null ? st.per[i].style : j); }
    function sst(i, j) { return st.per[i].s[j == null ? st.per[i].style : j]; }
    function metal() { return (o.getMetal && o.getMetal()) || 'silver'; }
    function panel(i) { return ui.querySelector('[data-panel="' + i + '"]'); }
    function sbox(i, j) { var p = panel(i); return p ? p.querySelector('[data-sp="' + (j == null ? st.per[i].style : j) + '"]') : null; }
    function sideLabel(pl) { return pl.side ? pl.side.charAt(0).toUpperCase() + pl.side.slice(1) : ''; }
    function plName(pl, i) { return pl.label || sideLabel(pl) || ('Place ' + (i + 1)); }
    function styleName(v, j) { return v.label || ((presetById(v.preset) || {}).label) || ('Style ' + (j + 1)); }
    function textLines(t) { return t.lines.filter(function (l) { return l.kind !== 'glyph'; }); }
    function sumLimits(t) { return textLines(t).reduce(function (a, l) { return a + l.limit; }, 0); }
    function labelled(t) { return t.lines.some(function (l) { return !!l.label; }); }

    /* the total is always the line limits added up: 16 per line x 3 lines = 48 */
    function hintFor(v) {
      var side = SIDE_COPY[v.side] || '';
      if (v.mode === 'initials') return 'Two initials, interlaced until they read as one. The piece composes once both letters are in.';
      if (v.mode === 'monogram') return (v.monogram.count === 3 ? 'Three initials: two side by side, the third below.' : 'Two initials, side by side.') + (side ? ' Set ' + side : '');
      if (v.mode === 'upload') {
        return (v.upload && v.upload.note) ||
          'A PNG or JPEG, up to 4 MB. Dark artwork on a plain ground reads best; we redraw it for the laser and send you the proof.';
      }
      if (v.mode === 'date') return '';
      var t = v.text, tl = textLines(t), n = tl.length, total = sumLimits(t), glyphs = t.lines.length - n;
      if (!n) return 'We read the sign from the night you were born' + (side ? ', ' + side : '.');
      var lim = tl[0].limit, same = tl.every(function (l) { return l.limit === lim; });
      var s = n === 1 ? 'Up to ' + lim + ' characters'
        : same ? 'Up to ' + n + ' lines of ' + lim + ' characters, ' + total + ' in all'
        : 'Up to ' + n + ' lines, ' + total + ' characters in all';
      if (glyphs) s += ', with your moon sign';
      return s + (side ? ', ' + side : '.');
    }

    function styleHTML(v, i, j) {
      var s = sst(i, j);
      if (v.mode === 'initials' || v.mode === 'monogram') {
        var max = v.mode === 'monogram' ? v.monogram.count : 2;
        return '<div class="cg-row"><input class="cg-input cg-letters" data-letters type="text" maxlength="' + max + '" autocapitalize="characters" autocomplete="off" spellcheck="false" placeholder="' + (max === 3 ? 'AHR' : 'AB') + '" aria-label="Your initials"></div>' +
          '<p class="cg-hint">' + esc(hintFor(v)) + '</p>';
      }
      if (v.mode === 'upload') {
        return '<div class="cg-upload">' +
          '<label class="cg-up__pick"><input type="file" accept="image/png,image/jpeg" data-art>' +
          '<span class="cg-up__btn mono">Choose a file</span></label>' +
          '<span class="cg-up__name" data-art-name>' + esc(s.artName || 'Nothing chosen yet') + '</span>' +
          '<button type="button" class="cg-up__rm mono" data-art-rm' + (s.art ? '' : ' hidden') + '>Remove</button></div>' +
          '<p class="cg-hint">' + esc(hintFor(v)) + '</p>';
      }
      if (v.mode === 'date') {
        return '<div class="cg-row"><input class="cg-input cg-night" data-night type="date" aria-label="The night"></div>' +
          '<p class="cg-hint" data-moon-sentence>Give us the date and we set that exact sky.</p>';
      }
      var t = v.text, h = '';
      var free = textLines(t).some(function (l) { return !l.font; });
      if (free && t.fonts.length > 1) {
        h += '<p class="cg-group">Lettering</p>';      // so the fonts read as fonts, not as another face
        h += '<div class="cg-fonts" data-fontchips>' + t.fonts.map(function (f) {
          var F = R().FONTS[f];
          return F ? '<button type="button" class="cg-font' + (f === s.font ? ' is-on' : '') + '" data-font="' + f + '">' + esc(F.label) + '</button>' : '';
        }).join('') + '</div>';
      }
      if (isCoords(v)) {
        // the two lines are still typeable; this just fills them from a real place
        h += '<div class="cg-line cg-line--place">' +
          '<input class="cg-input cg-place__in" data-place-in type="text" enterkeyhint="search" ' +
          'placeholder="Find a place" aria-label="Find a place"></div>';
      }
      h += '<div class="cg-lines">' + t.lines.map(function (l, k) {
        var name = l.label || ('Line ' + (k + 1));
        if (l.kind === 'glyph') {
          /* A moon sign is not something to pick off a list of twelve: most people do not know
             theirs. Ask instead for the night they were born and where, and read the sign off the
             sky. The place is for the timezone — the Moon changes sign every couple of days, so
             the hour only decides it near a crossing. Time is optional; without it we take noon
             and say so when that leaves the answer open. */
          return '<div class="cg-line cg-line--sky" data-line="' + k + '">' +
            '<div class="cg-sky" data-sky="' + k + '">' +
            '<input class="cg-input cg-place__in" data-sky-place type="text" enterkeyhint="search" ' +
              'placeholder="Where were you born?" aria-label="Birth place">' +
            '<div class="cg-sky__when">' +
              '<input class="cg-input" data-sky-date type="date" aria-label="Birth date">' +
              '<input class="cg-input" data-sky-time type="time" aria-label="Time of birth, optional">' +
            '</div>' +
            '<p class="cg-sky__out" data-sky-out aria-live="polite">' + esc(name) + '</p>' +
            '</div></div>';
        }
        var ph = l.label || (k === 0 ? 'A line of your own' : 'Line ' + (k + 1));
        return '<div class="cg-line" data-line="' + k + '"' + (k === 0 || labelled(t) ? '' : ' hidden') + '>' +
          '<input class="cg-input cg-line__in" data-line-in="' + k + '" type="text" autocomplete="off" spellcheck="false" enterkeyhint="next" placeholder="' + esc(ph) + '" aria-label="' + esc(name) + '">' +
          '<span class="cg-count" data-count="' + k + '" aria-hidden="true">0/' + l.limit + '</span>' +
          (t.slider ? '<label class="cg-size" data-size="' + k + '" hidden><span class="cg-size__a cg-size__a--s" aria-hidden="true">A</span>' +
            '<input type="range" min="0" max="100" step="1" value="100" data-size-in="' + k + '" aria-label="Size of ' + esc(name.toLowerCase()) + '">' +
            '<span class="cg-size__a cg-size__a--l" aria-hidden="true">A</span></label>' : '') +
          '</div>';
      }).join('') + '</div>';
      if (isLines(v) && textLines(t).length > 1) {
        h += '<label class="cg-match"><input type="checkbox" data-match' + (s.match ? ' checked' : '') + '>' +
          '<span>Match every line to one size</span></label>';
      }
      return h + '<p class="cg-hint" data-hint>' + esc(hintFor(v)) + '</p>';
    }

    function panelHTML(pl, i) {
      var list = stylesOf(pl), h = pl.sub ? '<p class="cg-hint cg-sub">' + esc(pl.sub) + '</p>' : '';
      if (list.length > 1) {
        h += '<div class="cg-fonts cg-styles" role="tablist">' + list.map(function (s, j) {
          return '<button type="button" role="tab" class="cg-font cg-style-tab' + (j === st.per[i].style ? ' is-on' : '') + '" data-style="' + j + '">' + esc(styleName(styleView(pl, j), j)) + '</button>';
        }).join('') + '</div>';
      }
      return h + list.map(function (s, j) {
        return '<div class="cg-style" data-sp="' + j + '"' + (j === st.per[i].style ? '' : ' hidden') + '>' + styleHTML(styleView(pl, j), i, j) + '</div>';
      }).join('');
    }

    function build() {
      var h = '<span class="opt-label">' + esc(o.heading || 'Compose your piece') + '</span>';
      if (P.length > 1) {
        h += '<div class="cg-fonts cg-places" role="tablist">' + P.map(function (pl, i) {
          return '<button type="button" role="tab" class="cg-font cg-place' + (i === st.active ? ' is-on' : '') + '" data-pl="' + i + '" aria-selected="' + (i === st.active) + '">' +
            esc(plName(pl, i)) + '<span class="cg-dot" aria-hidden="true"></span></button>';
        }).join('') + '</div>';
      }
      h += P.map(function (pl, i) { return '<div class="cg-panel" data-panel="' + i + '"' + (i === st.active ? '' : ' hidden') + '>' + panelHTML(pl, i) + '</div>'; }).join('');
      h += '<p class="cg-warn" data-cg-warn hidden></p>';
      ui.innerHTML = h;
      ui.hidden = false;
      wire();
    }

    /* ---- line editing: per-line limits, overflow flows to the next text line ---- */
    function lineInputs(i, j) { var b = sbox(i, j); return b ? b.querySelectorAll('[data-line-in]') : []; }
    function syncLineInputs(i, j) {
      var s = sst(i, j);
      lineInputs(i, j).forEach(function (inp) { var k = +inp.getAttribute('data-line-in'); if (inp.value !== s.lines[k].text) inp.value = s.lines[k].text; });
    }
    function nextText(t, k) { for (var j = k + 1; j < t.lines.length; j++) if (t.lines[j].kind !== 'glyph') return j; return -1; }
    function visibleLines(v, s) {
      var t = v.text, n = t.lines.length;
      if (labelled(t)) return n;                       // a named set (coordinates, place and date) shows every field
      var last = -1;
      s.lines.forEach(function (l, k) { if (l.text) last = k; });
      return Math.min(n, Math.max(1, s.shown || 1, last + 2));
    }
    function flow(v, s, k) {
      var t = v.text, moved = 0, j = k;
      while (j >= 0 && t.lines[j]) {
        var lim = t.lines[j].limit, c = chars(s.lines[j].text);
        if (c.length <= lim) break;
        var head = c.slice(0, lim).join(''), tail = c.slice(lim).join('');
        var sp = head.lastIndexOf(' ');
        if (tail && !/^\s/.test(tail) && sp > 0) { tail = head.slice(sp + 1) + tail; head = head.slice(0, sp); }
        tail = tail.replace(/^\s+/, '');
        s.lines[j].text = head.replace(/\s+$/, '');
        var n = nextText(t, j);
        if (n === -1) break;
        var nx = s.lines[n].text;
        s.lines[n].text = tail + (nx && tail ? ' ' : '') + nx;
        if (j === k) moved = clen(tail);
        j = n;
      }
      return moved;
    }
    function onLineInput(i, j, k, inp) {
      var v = sv(i, j), s = sst(i, j);
      var atEnd = inp.selectionStart === inp.value.length;
      s.lines[k].text = stripGlyphs(inp.value.replace(/[\r\n]+/g, ' '));
      var moved = flow(v, s, k);
      syncLineInputs(i, j);
      var n = nextText(v.text, k);
      if (moved > 0 && atEnd && n !== -1) {
        s.shown = Math.max(s.shown, n + 1);
        changed();
        var nx = sbox(i, j).querySelector('[data-line-in="' + n + '"]');
        if (nx) { nx.focus(); try { nx.setSelectionRange(moved, moved); } catch (e) {} }
        return;
      }
      changed();
    }
    function onLineKey(i, j, k, inp, e) {
      var v = sv(i, j), s = sst(i, j), b = sbox(i, j), n = nextText(v.text, k);
      if (e.key === 'Enter') {
        e.preventDefault();                            // never submit the product form from a line
        if (n !== -1) { s.shown = Math.max(s.shown, n + 1); refreshStyle(i); var el = b.querySelector('[data-line-in="' + n + '"]'); if (el) el.focus(); }
        return;
      }
      if (e.key === 'Backspace' && k > 0 && inp.selectionStart === 0 && inp.selectionEnd === 0) {
        var prev = null;
        for (var q = k - 1; q >= 0; q--) { var el2 = b.querySelector('[data-line-in="' + q + '"]'); if (el2) { prev = el2; break; } }
        if (!prev) return;
        e.preventDefault();
        if (!s.lines[k].text) s.shown = Math.min(s.shown, k);
        refreshStyle(i);
        prev.focus();
        try { var pos = prev.value.length; prev.setSelectionRange(pos, pos); } catch (err) {}
      }
    }

    function wire() {
      skyCtls.length = 0;
      ui.querySelectorAll('[data-pl]').forEach(function (b) {
        b.addEventListener('click', function () { setActive(+b.getAttribute('data-pl')); });
      });
      P.forEach(function (pl, i) {
        var p = panel(i);
        p.querySelectorAll('[data-style]').forEach(function (b) {
          b.addEventListener('click', function () { setStyle(i, +b.getAttribute('data-style')); });
        });
        stylesOf(pl).forEach(function (_, j) {
          var box = sbox(i, j), s = sst(i, j), v = sv(i, j);
          if (!box) return;
          var li = box.querySelector('[data-letters]');
          if (li) li.addEventListener('input', function () {
            var max = +li.getAttribute('maxlength') || 2;
            s.letters = li.value.replace(/[^a-zA-Z]/g, '').slice(0, max);
            li.value = s.letters.toUpperCase();
            changed();
          });
          var fi = box.querySelector('[data-art]');
          if (fi) fi.addEventListener('change', function () {
            var f = fi.files && fi.files[0];
            if (!f) return;
            if (!/^image\/(png|jpe?g)$/i.test(f.type)) { artNote(box, 'That file is not a PNG or JPEG.'); fi.value = ''; return; }
            if (f.size > 4 * 1024 * 1024) { artNote(box, 'That file is over 4 MB. Send a smaller one.'); fi.value = ''; return; }
            var rd = new FileReader();
            rd.onload = function () {
              s.art = String(rd.result); s.artName = f.name; s.artUrl = '';
              artNote(box, ''); changed(); refreshStyle(i);
            };
            rd.onerror = function () { artNote(box, 'That file could not be read.'); };
            rd.readAsDataURL(f);
          });
          var frm = box.querySelector('[data-art-rm]');
          if (frm) frm.addEventListener('click', function () {
            s.art = ''; s.artName = ''; s.artUrl = '';
            if (fi) fi.value = '';
            artNote(box, ''); changed(); refreshStyle(i);
          });
          var ni = box.querySelector('[data-night]');
          if (ni) ni.addEventListener('input', function () {
            s.night = ni.value; moonSentence(box, s.night);
            /* the night the customer sets on the moon face is the same night the sign is read from, so
               carry it to every moon-sign face (Back's "Name and moon sign", etc.) that they haven't
               dated themselves — the date field there fills in on its own. */
            if (ni.value) skyCtls.forEach(function (c) { c.fill(ni.value); });
            changed();
          });
          box.querySelectorAll('[data-font]').forEach(function (b) {
            b.addEventListener('click', function () {
              s.font = b.getAttribute('data-font');
              box.querySelectorAll('[data-font]').forEach(function (x) { x.classList.toggle('is-on', x === b); });
              changed();
            });
          });
          /* Coordinates: a place fills the two lines, which stay editable afterwards. */
          var pin = box.querySelector('[data-place-in]');
          if (pin && window.CoeuraPlaces) {
            var cl = coordLines(v.text);
            window.CoeuraPlaces.attach(pin, {
              api: o.api,
              onClear: function () { s.place = null; },
              onPick: function (pl) {
                s.place = pl;
                [[cl.lat, pl.latText], [cl.lng, pl.lngText]].forEach(function (pair) {
                  var k = pair[0];
                  if (k == null || !s.lines[k]) return;
                  s.lines[k].text = pair[1].slice(0, v.text.lines[k].limit);
                  var inp = box.querySelector('[data-line-in="' + k + '"]');
                  if (inp) { inp.value = s.lines[k].text; inp.closest('.cg-line').hidden = false; }
                  flow(v, s, k);
                });
                changed();
              }
            });
          }

          /* Moon sign: read off the sky from a place, a date and an optional time. */
          var skyBox = box.querySelector('[data-sky]');
          if (skyBox) {
            var gk = +skyBox.getAttribute('data-sky');
            var sp = skyBox.querySelector('[data-sky-place]');
            var sd = skyBox.querySelector('[data-sky-date]');
            var stm = skyBox.querySelector('[data-sky-time]');
            var out = skyBox.querySelector('[data-sky-out]');
            var skyAuto = false;   // true while this date is one the moon face filled in, not one the customer typed
            var readSky = function () {
              var sk = s.sky, M = window.COMoon;
              if (!M || !M.sign) return;
              if (!sk.date) { s.lines[gk].text = ''; out.textContent = 'Your birthday, and where.'; changed(); return; }
              if (!sk.place) { s.lines[gk].text = ''; out.textContent = 'And the place, so we know the hour.'; changed(); return; }
              var d = sk.date.split('-'), tm = (sk.time || '12:00').split(':');
              var when = M.zonedTime(+d[0], +d[1], +d[2], +tm[0], +tm[1], sk.place.tz);
              if (isNaN(when)) { out.textContent = 'That date did not read.'; return; }
              var sg = M.sign(when);
              s.lines[gk].text = sg.char;
              s.sky.sign = sg.id;
              /* If no time was given we used noon, so say when the day is split between two signs. */
              var open = '';
              if (!sk.time) {
                var a = M.sign(M.zonedTime(+d[0], +d[1], +d[2], 0, 0, sk.place.tz));
                var b = M.sign(M.zonedTime(+d[0], +d[1], +d[2], 23, 59, sk.place.tz));
                if (a.id !== b.id) open = ' The Moon moved from ' + a.label + ' to ' + b.label +
                  ' that day, so the time of day decides it.';
              }
              /* show Patti's own glyph, the same artwork that gets engraved, not the Unicode character */
              var art = R().signArt(sg.id);
              out.innerHTML = '<img class="cg-sky__glyph" src="' + esc(art) + '" alt="" aria-hidden="true">' +
                esc('The Moon was in ' + sg.label + '.' + open);
              changed();
            };
            if (sp && window.CoeuraPlaces) {
              window.CoeuraPlaces.attach(sp, {
                api: o.api,
                onClear: function () { s.sky.place = null; readSky(); },
                onPick: function (pl) { s.sky.place = pl; readSky(); }
              });
            }
            if (sd) sd.addEventListener('input', function () { s.sky.date = sd.value; skyAuto = false; readSky(); });
            if (stm) stm.addEventListener('input', function () { s.sky.time = stm.value; readSky(); });
            /* let the moon face autofill this date, unless the customer has typed one here themselves */
            skyCtls.push({ fill: function (v) {
              if (s.sky.date && !skyAuto) return;
              s.sky.date = v; skyAuto = true;
              if (sd) sd.value = v;
              readSky();
            } });
          }
          var mt = box.querySelector('[data-match]');
          if (mt) mt.addEventListener('change', function () { s.match = mt.checked; changed(); });
          box.querySelectorAll('[data-line-in]').forEach(function (inp) {
            var k = +inp.getAttribute('data-line-in');
            /* A line named "Date" (Back's "Place, date, sentiment") is written in from the night the
               customer set on the moon face: the same night, so they never type it twice (Patti 10/16).
               Like the moon sign above, it fills only while the line is empty or still holds a date
               the moon face put there; once the customer types into it, it is theirs. */
            var cl = v.text && v.text.lines[k], dateAuto = false;
            if (cl && cl.kind !== 'glyph' && /^date$/i.test(String(cl.label || '').trim())) {
              skyCtls.push({ fill: function (night) {
                if (s.lines[k].text && !dateAuto) return;
                var txt = engravedDate(night, cl.limit);
                if (!txt) return;
                s.lines[k].text = txt; dateAuto = true; s.autoDate = k;
                inp.value = txt;
              } });
              inp.addEventListener('input', function () { dateAuto = false; s.autoDate = -1; });
            }
            inp.addEventListener('input', function () { onLineInput(i, j, k, inp); });
            inp.addEventListener('keydown', function (e) { onLineKey(i, j, k, inp, e); });
          });
          box.querySelectorAll('[data-size-in]').forEach(function (r) {
            var k = +r.getAttribute('data-size-in');
            r.addEventListener('input', function () { s.lines[k].t = (+r.value) / 100; changed(); });
          });
        });
      });
    }
    /* "2026-10-14" as it is engraved: "14 OCTOBER 2026", day first, in capitals (Patti 10/16).
       The longest, "30 SEPTEMBER 2026", is 17 characters, inside the line's 18; a line set shorter
       than the long form gets "14.10.2026", still day first. */
    var MONTHS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST',
                  'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
    function engravedDate(night, limit) {
      var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(night || '');
      if (!m) return '';
      var long = (+m[3]) + ' ' + MONTHS[+m[2] - 1] + ' ' + m[1];
      return (!limit || long.length <= limit) ? long : m[3] + '.' + m[2] + '.' + m[1];
    }
    function moonSentence(box, night) {
      var s = box.querySelector('[data-moon-sentence]');
      if (!s || !window.COMoon || !night) return;
      var d = new Date(night + 'T22:00:00Z');
      if (!isNaN(d)) s.textContent = window.COMoon.sentence(d);
    }
    function setStyle(i, j) {
      st.per[i].style = Math.max(0, Math.min(stylesOf(P[i]).length - 1, j));
      var p = panel(i);
      p.querySelectorAll('[data-style]').forEach(function (b) { b.classList.toggle('is-on', +b.getAttribute('data-style') === st.per[i].style); });
      p.querySelectorAll('[data-sp]').forEach(function (x) { x.hidden = +x.getAttribute('data-sp') !== st.per[i].style; });
      changed();
    }
    function setActive(i) {
      st.active = Math.max(0, Math.min(P.length - 1, i));
      st.per[st.active].seen = true;   // the customer has looked at this face (see started)
      ui.querySelectorAll('[data-pl]').forEach(function (b) {
        var on = +b.getAttribute('data-pl') === st.active;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      ui.querySelectorAll('[data-panel]').forEach(function (p) { p.hidden = +p.getAttribute('data-panel') !== st.active; });
      changed();
    }
    function changed() { if (dead) return; render(); if (o.onChange) o.onChange(); }

    /* ---- stage ---- */
    /* Centre the monogram on its face, in two passes.
       Pass 1, instant: getBBox(). Correct in Chrome, and correct for a single letter everywhere.
       Pass 2, the painted pixels. WebKit, which is every browser on an iPhone, lays out SVG text as
       though the Sappho ligature had not fired: text-anchor:middle centres the width of two SEPARATE
       letters, then paints the narrower interlaced glyph from that start point, so a two-letter
       monogram lands ~11 units left of the face. getBBox(), getExtentOfChar() and canvas
       measureText() all report that same un-ligated layout, so none of them can see the error; only
       the painting knows where the ink is. So rasterise the monogram alone and centre on its pixels.
       Measured 10/6: 6 of the last 10 initials proofs were 5 to 8 units left this way (Kevin, "the
       first single letter sits properly"). Horizontal only, so the approved vertical placement is
       untouched. Returns a promise so the proof can wait for the corrected position.
       No visible jump: a pair is painted ONCE, already centred. The first time a pair appears it is held
       transparent while its fonts land and its ink is measured (~1 frame once the fonts are warm), then
       fades in; the correction is kept per pair (as a share of the font size, so it holds on every
       piece and metal), so later redraws of that pair place it instantly. Kevin 10/6: "the letters
       shake when you type in the second letter" (they painted bbox-centred, then hopped ~10 units). */
    var MONO_DX = {};
    function centerMono(svg, box) {
      var g = svg && svg.querySelector('.cg-mono');
      if (!g || !box) return Promise.resolve();
      var t = g.querySelector('text'), pair = t ? t.textContent : '', fs = t ? parseFloat(t.getAttribute('font-size')) || 0 : 0;
      var cx = box.x + box.w / 2, cy = box.y + box.h / 2, fams = monoFamilies(g);
      var tok = (svg.__cgMono = (svg.__cgMono || 0) + 1);
      function current() { return svg.__cgMono === tok && svg.querySelector('.cg-mono') === g; }   // a newer letter wins
      function layoutBox() {
        var tf = g.getAttribute('transform'), bb = null;
        g.removeAttribute('transform');
        try { bb = g.getBBox(); } catch (e) {}
        if (tf) g.setAttribute('transform', tf);
        return bb && bb.width ? bb : null;
      }
      function place(dx) {   // dx: how far right of the layout box's centre the ink is painted
        var bb = layoutBox();
        if (bb) g.setAttribute('transform', 'translate(' + (cx - (bb.x + bb.width / 2) - dx).toFixed(3) + ' ' + (cy - (bb.y + bb.height / 2)).toFixed(3) + ')');
      }
      var known = pair.length < 2 ? 0 : MONO_DX[pair];   // a single letter has no ligature to correct
      if (known != null && monoFontsIn(fams)) { place(known * fs); return Promise.resolve(); }
      g.style.opacity = '0';
      place(known != null ? known * fs : 0);
      function show() { if (svg.querySelector('.cg-mono') === g) { g.style.transition = 'opacity .16s ease-out'; g.style.opacity = ''; } }
      var late = setTimeout(show, 700);   // never hold the letters back long; a late measurement still lands
      function done() { clearTimeout(late); show(); }
      return monoFontsLoad(fams).then(function () {
        if (!current()) return;
        if (known != null) { place(known * fs); return; }
        var bb = layoutBox();
        return paintedInkX(svg, g).then(function (ink) {
          if (!current()) return;
          if (ink && bb && fs) MONO_DX[pair] = ((ink.l + ink.r) / 2 - (bb.x + bb.width / 2)) / fs;
          place(MONO_DX[pair] != null ? MONO_DX[pair] * fs : 0);
        });
      }).then(done, done);
    }
    function monoFamilies(g) {
      var f = {};
      Array.prototype.forEach.call(g.querySelectorAll('text'), function (t) { var n = t.getAttribute('font-family'); if (n) f[n] = 1; });
      return Object.keys(f);
    }
    function monoFontsIn(fams) {
      try { return !document.fonts || fams.every(function (f) { return document.fonts.check('48px "' + f + '"'); }); } catch (e) { return true; }
    }
    /* font-display:block lays the letters out in a fallback font until the real one lands, so a box
       measured before then is wrong vertically as well: wait for them, capped like the ink pass */
    function monoFontsLoad(fams) {
      if (!document.fonts || !document.fonts.load) return Promise.resolve();
      var all = Promise.all(fams.map(function (f) { return document.fonts.load('48px "' + f + '"'); }));
      var cap = new Promise(function (res) { setTimeout(res, 2500); });
      return Promise.race([all, cap]).catch(function () {});
    }
    /* Left and right edge of the monogram's painted pixels, in the overlay's own units, with its
       transform ignored. Fonts are inlined because an SVG drawn as an image cannot reach the page's
       fonts (the same reason the proof inlines them). Capped at 2.5s so a slow font can never hold up
       the stage or the proof; on timeout pass 1 simply stands. */
    function paintedInkX(svg, g) {
      var vb = svg.viewBox && svg.viewBox.baseVal;
      if (!vb || !vb.width || !vb.height || !window.XMLSerializer || !window.Blob) return Promise.resolve(null);
      var clone = g.cloneNode(true); clone.removeAttribute('transform'); clone.removeAttribute('style');   // not the fade-in hold
      var markup = new XMLSerializer().serializeToString(clone);
      var work = fontCSS(markup).then(function (css) {
        var W = 600, H = Math.max(1, Math.round(W * vb.height / vb.width));
        var doc = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="' + vb.x + ' ' + vb.y + ' ' + vb.width + ' ' + vb.height +
          '" width="' + W + '" height="' + H + '"><style>' + css + ' text{fill:#000!important;stroke:none!important}</style>' +
          markup + '</svg>';
        var url = URL.createObjectURL(new Blob([doc], { type: 'image/svg+xml' }));
        return loadImg(url, false).then(function (im) {
          var cv = document.createElement('canvas'); cv.width = W; cv.height = H;
          var c = cv.getContext('2d'); c.drawImage(im, 0, 0, W, H); URL.revokeObjectURL(url);
          var d = c.getImageData(0, 0, W, H).data, L = W, R = -1;
          for (var y = 0; y < H; y++) for (var x = 0; x < W; x++) if (d[(y * W + x) * 4 + 3] > 40) { if (x < L) L = x; if (x > R) R = x; }
          return R < 0 ? null : { l: vb.x + L * vb.width / W, r: vb.x + (R + 1) * vb.width / W };
        }, function () { URL.revokeObjectURL(url); return null; });
      });
      var cap = new Promise(function (res) { setTimeout(function () { res(null); }, 2500); });
      return Promise.race([work, cap]).catch(function () { return null; });
    }
    /* letter tones: plate pieces show silver for Mixed Metals; the blank charm keeps it */
    /* letters follow the metal the customer chose, so Mixed Metals stays two-tone even on a photo; only the
       PHOTO falls back to the silver shot when a piece has no Mixed Metals photo of its own (see plateKey) */
    function toneFor(kind, m) { return m; }
    function plateMetal(m) { return m === 'mixed' ? 'silver' : m; }
    function layFor(i, j) {
      j = j == null ? st.per[i].style : j;
      var v = sv(i, j);
      if (v.mode !== 'engraving') return null;
      if (i === st.active && j === st.per[i].style && lays[i][j]) return lays[i][j];
      var m = metal(), sf = stageFor(prod, P[i], m, o.assetBase, nightOf(P[i], sst(i, j))), b = R().pickBox(v, sf.key || m);
      if (!b) return null;
      return R().layoutText(R().unitBox(b, sf.vh), R().withMatch(v.text, sst(i, j)), sst(i, j).lines, sst(i, j).font);
    }
    function render() {
      if (dead) return;
      /* no face chosen yet (a multi-face piece at rest): the product's own photo, nothing composed over it */
      if (st.active < 0) {
        if (o.overlay) { o.overlay.innerHTML = ''; o.overlay.style.visibility = ''; }
        if (o.stage) o.stage.style.visibility = '';
        if (o.onShowBase) o.onShowBase();
        P.forEach(function (_, k) { refreshStyle(k); });
        refreshTabs();
        refreshWarn();
        return;
      }
      var i = st.active, j = st.per[i].style, pl = P[i], v = sv(i, j), s = sst(i, j), m = metal();
      var sf = stageFor(prod, pl, m, o.assetBase, nightOf(pl, s)), ov = o.overlay, stg = o.stage;
      if (ov) ov.style.visibility = '';
      lays[i][j] = null;
      if (!started(i)) {
        if (ov) ov.innerHTML = '';
        /* Once a face is picked on a multi-face piece, show THAT face's own photo (Front / Back / Inside /
           the moon) even before they compose, so choosing a face changes the picture. A single-face piece
           has no face to choose, so it rests on the product's first photo until they type. */
        if (P.length > 1 && sf.kind === 'plate' && sf.src) {
          if (stg) { stg.style.visibility = ''; if (stg.getAttribute('src') !== sf.src) stg.setAttribute('src', sf.src); }
        } else {
          if (stg) stg.style.visibility = '';
          if (o.onShowBase) o.onShowBase();
        }
        P.forEach(function (_, k) { refreshStyle(k); });
        refreshTabs();
        refreshWarn();
        return;
      }
      if (sf.kind === 'none') {
        if (ov) ov.innerHTML = '';
        if (o.onShowBase) o.onShowBase();
        lays[i][j] = layFor(i, j);
      } else if (sf.kind === 'blank') {
        if (stg) stg.style.visibility = 'hidden';
        if (ov) {
          ov.setAttribute('viewBox', '0 0 100 100');
          if (v.mode === 'initials' && prod.blank) {
            ov.innerHTML = R().charmStage(s.letters, m);                 // the charm: the piece IS the letters
            centerMono(ov, R().unitBox(R().pickBox(v, m) || { x: 25, y: 25, w: 50, h: 50 }, 100));
          } else {
            var bres = R().renderPlacement(v, s, { boxKey: sf.key, metal: m === 'mixed' ? 'silver' : m, toneMetal: m, tone: v.tone, vh: 100, form: prod.form });
            ov.innerHTML = BLANK_GROUND + bres.svg;
            if (v.mode === 'initials') centerMono(ov, bres.box);
            lays[i][j] = bres.layout;
          }
        }
      } else {
        if (stg) { stg.style.visibility = ''; if (stg.getAttribute('src') !== sf.src) stg.setAttribute('src', sf.src); }
        var res = R().renderPlacement(v, s, { boxKey: sf.key, metal: plateMetal(m), toneMetal: m, tone: v.tone, vh: sf.vh, form: prod.form, handle: prod.handle });
        if (ov) {
          ov.setAttribute('viewBox', '0 0 100 ' + sf.vh.toFixed(3));
          ov.innerHTML = res.svg;
          if (v.mode === 'initials') centerMono(ov, res.box);
        }
        lays[i][j] = res.layout;
      }
      P.forEach(function (_, k) { refreshStyle(k); });
      refreshTabs();
      refreshWarn();
    }
    function artNote(box, msg) {
      var el = box.querySelector('[data-art-note]');
      if (!el && msg) {
        el = document.createElement('p');
        el.className = 'cg-hint cg-up__err';
        el.setAttribute('data-art-note', '');
        box.appendChild(el);
      }
      if (el) { el.textContent = msg || ''; el.hidden = !msg; }
    }
    function refreshStyle(i) {
      var j = st.per[i].style, v = sv(i, j), box = sbox(i, j), s = sst(i, j);
      if (box && v.mode === 'upload') {
        var nm = box.querySelector('[data-art-name]'), rm = box.querySelector('[data-art-rm]');
        if (nm) nm.textContent = s.artName || 'Nothing chosen yet';
        if (rm) rm.hidden = !s.art;
        return;
      }
      if (!box || v.mode !== 'engraving') return;
      var t = v.text, vis = visibleLines(v, s), lay = i === st.active ? lays[i][j] : null;
      box.querySelectorAll('[data-line]').forEach(function (row) {
        var k = +row.getAttribute('data-line');
        if (t.lines[k] && t.lines[k].kind === 'glyph') return;
        row.hidden = k >= vis;
        var c = row.querySelector('[data-count]');
        if (c) c.textContent = clen(s.lines[k].text) + '/' + t.lines[k].limit;
        var L = lay && lay.lines.filter(function (x) { return x.i === k; })[0];
        var sz = row.querySelector('[data-size]');
        if (sz) {
          sz.hidden = !(L && !L.wide && L.hi > L.lo * 1.03);
          var r = sz.querySelector('input'), val = Math.round((s.lines[k].t == null ? 1 : s.lines[k].t) * 100);
          if (+r.value !== val) r.value = val;
        }
        row.classList.toggle('is-wide', !!(L && L.wide));
        var mtc = box.querySelector('[data-match]');
        if (mtc && mtc.checked !== !!s.match) mtc.checked = !!s.match;
        var inp = row.querySelector('[data-line-in]');   // a caps-locked lettering types in capitals too
        if (inp && R().capsOn) inp.style.textTransform = R().capsOn(t, R().lineFont(t, k, s.font)) ? 'uppercase' : '';
      });
    }
    function refreshTabs() {
      ui.querySelectorAll('[data-pl]').forEach(function (b) { b.classList.toggle('is-filled', started(+b.getAttribute('data-pl'))); });
    }
    function refreshWarn() {
      var w = ui.querySelector('[data-cg-warn]');
      if (!w) return;
      if (st.active < 0) { w.hidden = true; return; }
      var i = st.active, j = st.per[i].style, v = sv(i, j), lay = lays[i][j];
      var wide = lay && lay.warns.length ? lay.warns[0] : null;
      if (!wide) { w.hidden = true; w.textContent = ''; return; }
      var n = v.text ? v.text.lines.length : 1;
      w.textContent = (n > 1 ? 'Line ' + (wide.line + 1) + ' runs' : 'That line runs') + ' past the edge in this lettering. Shorten it' +
        (nextText(v.text, wide.line) !== -1 ? ' or move a word to the next line.' : '.');
      w.hidden = false;
    }

    /* ---- readiness ---- */
    function started(i) {
      var j = st.per[i].style, v = sv(i, j), s = sst(i, j);
      if (v.mode === 'initials' || v.mode === 'monogram') return !!s.letters;
      if (v.mode === 'date') return !!s.night;
      if (v.mode === 'upload') return !!s.art;
      /* a date the moon face wrote in waits on its face as a suggestion: it counts toward the piece only
         once the customer has opened that face and seen it, so nobody is engraved on a face they never
         looked at. Anything they typed counts at once, as always. */
      return s.lines.some(function (l, k) { return l.text.trim() && (k !== s.autoDate || !!st.per[i].seen); });
    }
    /* Has the customer put anything on this piece at all? Every product is buyable plain, so this is
       what separates "add the piece as it is" from "this is a commission" — it gates the acknowledgment
       and the proof, and it is why attachForm below leaves an untouched form alone. */
    function startedAny() { return P.some(function (_, i) { return started(i); }); }
    function plReady(i) {
      var j = st.per[i].style, v = sv(i, j), s = sst(i, j);
      if (v.mode === 'initials') return s.letters.replace(/[^a-zA-Z]/g, '').length === 2;
      if (v.mode === 'monogram') return s.letters.replace(/[^a-zA-Z]/g, '').length === v.monogram.count;
      if (v.mode === 'date') return !!s.night;
      if (v.mode === 'upload') return !!s.art;
      if (!started(i)) return false;
      var L = layFor(i, j);
      return !(L && L.warns.length);
    }
    function waitLabel(i) {
      var v = sv(i);
      return started(i) && v.mode === 'engraving' ? 'Shorten your words' : (MODE_WAIT[v.mode] || 'Compose it first');
    }
    /* the placements that go on the order: the active one ('one'), or every one with content ('any') */
    function chosen() {
      if (prod.choice !== 'any') return [st.active];
      return P.map(function (_, i) { return i; }).filter(function (i) { return started(i); });
    }
    function status() {
      if (prod.choice !== 'any') return plReady(st.active) ? { ready: true } : { ready: false, label: waitLabel(st.active) };
      var any = false;
      for (var j = 0; j < P.length; j++) {
        if (plReady(j)) { any = true; continue; }
        if (P[j].required || started(j)) return { ready: false, label: waitLabel(j), place: j };
      }
      return any ? { ready: true } : { ready: false, label: waitLabel(0) };
    }

    /* ---- what the order carries ---- */
    function spec() {
      var m = metal(), out = { v: 3, product: (o.meta && o.meta.handle) || prod.handle, form: prod.form, metal: m, choice: prod.choice, placements: [] };
      chosen().forEach(function (i) {
        var pl = P[i], j = st.per[i].style, v = sv(i, j), s = sst(i, j), sf = stageFor(prod, pl, m, o.assetBase, nightOf(pl, s));
        var e = { id: pl.id, label: plName(pl, i), side: pl.side, mode: v.mode, box: R().pickBox(v, sf.key || m), plate: sf.src || null };
        if (stylesOf(pl).length > 1 || v.preset) { e.style = styleName(v, j); if (v.preset) e.preset = v.preset; }
        if (v.mode === 'initials' || v.mode === 'monogram') {
          e.letters = s.letters.toUpperCase();
          if (v.mode === 'monogram') e.monogram = { count: v.monogram.count, font: R().FONTS[v.monogram.font] ? R().FONTS[v.monogram.font].label : v.monogram.font };
        } else if (v.mode === 'date') {
          e.night = s.night;
          if (sf.frame) e.frame = sf.frame;      // which of John's rendered nights was shown
        } else if (v.mode === 'upload') {
          e.artwork = { file: s.artName || '', url: s.artUrl || '' };   // the customer's own artwork
        } else {
          var L = layFor(i, j);
          e.orientation = v.text.orientation;
          if (isLines(v) && textLines(v.text).length > 1) e.matched = !!s.match;
          if (v.text.fieldMm) e.fieldMm = v.text.fieldMm;
          if (s.place) e.place = { label: s.place.label, lat: s.place.lat, lng: s.place.lng };
          e.lines = (L ? L.lines : []).map(function (l) {
            var cl = v.text.lines[l.i], raw = s.lines[l.i].text;
            if (cl && cl.kind === 'glyph') {
              var g = glyphBy(raw);
              var gx = { line: l.i + 1, kind: 'glyph', sign: g ? g.id : raw, text: g ? g.label : raw, size: +l.fs.toFixed(3) };
              /* the sky it was read from, so the atelier can check the sign against the birth */
              if (s.sky && s.sky.place && s.sky.date) {
                gx.read = { place: s.sky.place.label, tz: s.sky.place.tz, date: s.sky.date,
                  time: s.sky.time || '', assumedNoon: !s.sky.time };
              }
              return gx;
            }
            var x = { line: l.i + 1, text: l.text, font: R().FONTS[l.font].label, size: +l.fs.toFixed(3), scale: +(s.lines[l.i].t == null ? 1 : s.lines[l.i].t).toFixed(2) };
            if (cl && cl.label) x.field = cl.label;
            if (l.capMm) x.capMm = +l.capMm.toFixed(2);
            return x;
          });
        }
        out.placements.push(e);
      });
      return out;
    }
    /* [name, value] pairs for line-item properties; '_' names are hidden from the customer at checkout */
    function properties() {
      var list = [], idx = chosen(), multi = idx.length > 1;
      idx.forEach(function (i) {
        var pl = P[i], j = st.per[i].style, v = sv(i, j), s = sst(i, j), pre = multi ? plName(pl, i) + ' · ' : '';
        if (stylesOf(pl).length > 1) list.push([pre + 'Layout', styleName(v, j)]);
        if (v.tone && v.mode === 'initials') list.push([pre + 'Initials metal', TONE_LABEL[v.tone] || v.tone]);
        if (v.mode === 'initials') list.push([pre + 'Letters', s.letters.toUpperCase()]);
        else if (v.mode === 'monogram') list.push([pre + 'Initials', s.letters.toUpperCase().split('').join(' ')]);
        else if (v.mode === 'date') list.push([pre + 'Night', s.night]);
        else if (v.mode === 'upload') list.push([pre + 'Artwork', s.artName || 'Uploaded file']);
        else {
          var L = layFor(i, j), texts = [], signs = [], fonts = [];
          (L ? L.lines : []).forEach(function (l) {
            var cl = v.text.lines[l.i];
            if (cl && cl.kind === 'glyph') { var g = glyphBy(s.lines[l.i].text); if (g) signs.push(g.label); return; }
            texts.push(cl && cl.label ? cl.label + ': ' + l.text : l.text);
            var f = R().FONTS[l.font].label;
            if (fonts.indexOf(f) === -1) fonts.push(f);
          });
          if (texts.length) list.push([pre + 'Inscription', texts.join(' / ')]);
          if (signs.length) list.push([pre + (signs.length > 1 ? 'Moon signs' : 'Moon sign'), signs.join(', ')]);
          if (signs.length && s.sky && s.sky.place && s.sky.date) {
            list.push([pre + 'Born', s.sky.date + (s.sky.time ? ', ' + s.sky.time : '') + ' · ' + s.sky.place.label]);
          }
          if (s.place) list.push([pre + 'Place', s.place.label]);
          if (fonts.length) list.push([pre + 'Lettering', fonts.join(', ')]);
        }
      });
      if (prod.choice !== 'any' && P.length > 1) {
        var a = P[st.active];
        list.push(['Placement', plName(a, st.active) + (a.side ? ' (' + a.side + ')' : '')]);
      }
      list.push(['_Engraving', JSON.stringify(spec())]);
      return list;
    }

    /* ---- the proof: plate + engraving exactly as composed, rasterized and uploaded at add-to-cart ---- */
    var FONT_DATA = {};
    function fontDataURL(file) {
      if (!FONT_DATA[file]) {
        FONT_DATA[file] = fetch(resolveSrc(file, o.assetBase)).then(function (r) { if (!r.ok) throw new Error('font'); return r.blob(); })
          .then(function (b) {
            return new Promise(function (res) {
              var fr = new FileReader();
              fr.onload = function () { res(fr.result); };
              fr.onerror = function () { res(null); };
              fr.readAsDataURL(b);
            });
          }).catch(function () { return null; });
      }
      return FONT_DATA[file];
    }
    /* @font-face rules (data URIs) for every family the markup uses: an SVG drawn as an image can't reach page fonts */
    function fontCSS(svg) {
      var fams = {}, FF = R().FONT_FILES;
      svg.replace(/font-family="([^"]+)"/g, function (all, list) {
        list.split(',').forEach(function (f) { f = f.trim().replace(/^['"]|['"]$/g, ''); if (FF[f]) fams[f] = FF[f]; });
        return all;
      });
      return Promise.all(Object.keys(fams).map(function (f) {
        return fontDataURL(fams[f]).then(function (d) { return d ? "@font-face{font-family:'" + f + "';src:url(" + d + ');}' : ''; });
      })).then(function (a) { return a.join(''); });
    }
    /* An SVG drawn as an image can reach nothing outside itself — the same reason the fonts above
       are inlined. The moon face is an <image>, so inline it too or the proof shows a bare plate. */
    var IMG_DATA = {};
    function imgDataURL(url) {
      if (!IMG_DATA[url]) {
        IMG_DATA[url] = fetch(url).then(function (r) { if (!r.ok) throw new Error('img'); return r.blob(); })
          .then(function (b) {
            return new Promise(function (res) {
              var fr = new FileReader();
              fr.onload = function () { res(fr.result); };
              fr.onerror = function () { res(null); };
              fr.readAsDataURL(b);
            });
          }).catch(function () { return null; });
      }
      return IMG_DATA[url];
    }
    function inlineImages(svg) {
      var urls = {};
      svg.replace(/<image\b[^>]*?\shref="([^"]+)"/g, function (all, u) { if (!/^data:/.test(u)) urls[u] = 1; return all; });
      var list = Object.keys(urls);
      if (!list.length) return Promise.resolve(svg);
      return Promise.all(list.map(function (u) {
        return imgDataURL(u).then(function (d) { return [u, d]; });
      })).then(function (pairs) {
        pairs.forEach(function (pr) {
          if (pr[1]) svg = svg.split('"' + pr[0] + '"').join('"' + pr[1] + '"');
        });
        return svg;
      });
    }
    function loadImg(src, cors) {
      return new Promise(function (res, rej) {
        var im = new Image();
        if (cors) im.crossOrigin = 'anonymous';
        im.onload = function () { res(im); };
        im.onerror = rej;
        im.src = src;
      });
    }
    function bust(u) { return u + (u.indexOf('?') === -1 ? '?' : '&') + 'cgproof=1'; }
    /* render one placement off-screen so the monogram is measured + optically centred exactly as on the stage */
    function overlayMarkup(i, vh, m) {
      var j = st.per[i].style, v = sv(i, j), s = sst(i, j), sf = stageFor(prod, P[i], m, o.assetBase, nightOf(P[i], s)), ns = 'http://www.w3.org/2000/svg';
      var tmp = document.createElementNS(ns, 'svg');
      tmp.setAttribute('viewBox', '0 0 100 ' + vh);
      tmp.setAttribute('width', '1000');
      tmp.setAttribute('height', String(Math.round(10 * vh)));
      tmp.style.cssText = 'position:absolute;left:-10000px;top:0;visibility:hidden';
      document.body.appendChild(tmp);
      var centred = Promise.resolve();
      if (sf.kind === 'blank' && v.mode === 'initials' && prod.blank) {
        tmp.innerHTML = R().charmStage(s.letters, m);
        centred = centerMono(tmp, R().unitBox(R().pickBox(v, m) || { x: 25, y: 25, w: 50, h: 50 }, 100));
      } else if (sf.kind === 'blank') {
        var bl = R().renderPlacement(v, s, { boxKey: sf.key, metal: m === 'mixed' ? 'silver' : m, toneMetal: m, tone: v.tone, vh: vh, form: prod.form });
        tmp.innerHTML = BLANK_GROUND + bl.svg;
        if (v.mode === 'initials') centred = centerMono(tmp, bl.box);
      } else {
        var res = R().renderPlacement(v, s, { boxKey: sf.key, metal: plateMetal(m), toneMetal: m, tone: v.tone, vh: vh, form: prod.form, handle: prod.handle });
        tmp.innerHTML = res.svg;
        if (v.mode === 'initials') centred = centerMono(tmp, res.box);
      }
      function take() { var inner = tmp.innerHTML; if (tmp.parentNode) document.body.removeChild(tmp); return inner; }
      return centred.then(take, take);
    }
    function panelCanvas(i, m) {
      var sf = stageFor(prod, P[i], m, o.assetBase, nightOf(P[i], sst(i))), v = sv(i);
      if (sf.kind === 'none') return Promise.resolve(null);
      var W = 1000, vh = sf.vh, H = Math.round(W * vh / 100);
      var cv = document.createElement('canvas'); cv.width = W; cv.height = H;
      var cx = cv.getContext('2d');
      cx.fillStyle = '#EDE6D9'; cx.fillRect(0, 0, W, H);
      var inner;
      var plate = sf.kind === 'plate' ? loadImg(bust(sf.src), true).then(function (im) { cx.drawImage(im, 0, 0, W, H); }) : Promise.resolve();
      return Promise.all([overlayMarkup(i, vh, m), plate]).then(function (a) { inner = a[0]; return inlineImages(inner); })
        .then(function (withImgs) { inner = withImgs; return fontCSS(inner); })
        .then(function (css) {
        /* xmlns:xlink is not decoration: a blob SVG is parsed as strict XML, and the renderer writes
           <image href=… xlink:href=…> on the moon face and the zodiac glyphs for older viewers. Without
           the declaration the document fails to parse ("Namespace prefix xlink for href on image is not
           defined"), the raster never loads, and the piece goes into the cart with no proof at all. */
        var svg = '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 100 ' + vh.toFixed(3) + '" width="' + W + '" height="' + H + '"><style>' + css + '</style>' + inner + '</svg>';
        var url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
        return loadImg(url, false).then(function (im) {
          return new Promise(function (res) {
            setTimeout(function () {                   // let the embedded faces settle before drawing
              cx.globalCompositeOperation = v.mode === 'engraving' || v.mode === 'monogram' ? 'multiply' : 'source-over';
              cx.drawImage(im, 0, 0, W, H);
              cx.globalCompositeOperation = 'source-over';
              URL.revokeObjectURL(url);
              res({ canvas: cv, pl: P[i], i: i });
            }, 120);
          });
        });
      });
    }
    function wrapText(c, text, maxW) {
      var words = String(text).split(/\s+/), lines = [], cur = '';
      words.forEach(function (w) {
        var t = cur ? cur + ' ' + w : w;
        if (c.measureText(t).width > maxW && cur) { lines.push(cur); cur = w; } else cur = t;
      });
      if (cur) lines.push(cur);
      return lines;
    }
    function proofImage() {
      var m = metal(), idx = chosen();
      return Promise.all(idx.map(function (i) { return panelCanvas(i, m).catch(function () { return null; }); })).then(function (ps) {
        ps = ps.filter(Boolean);
        if (!ps.length) return null;
        var gap = 28, pad = 40, head = ps.length > 1 ? 36 : 0;
        var H = Math.max.apply(null, ps.map(function (p) { return p.canvas.height; }));
        var W = ps.reduce(function (a, p) { return a + p.canvas.width; }, 0) + gap * (ps.length - 1) + pad * 2;
        var meas = document.createElement('canvas').getContext('2d');
        meas.font = '22px Pitch, "Courier New", monospace';
        var desc = properties().filter(function (p) { return p[0].charAt(0) !== '_'; }).map(function (p) { return p[0] + ': ' + p[1]; }).join('  |  ');
        var dl = wrapText(meas, desc, W - pad * 2).slice(0, 4);
        var cv = document.createElement('canvas');
        cv.width = W; cv.height = pad + head + H + 52 + 42 + dl.length * 32 + 40;
        var c = cv.getContext('2d');
        c.fillStyle = '#F8F5F0'; c.fillRect(0, 0, cv.width, cv.height);
        c.textAlign = 'left'; c.textBaseline = 'alphabetic';
        var x = pad, top = pad + head;
        ps.forEach(function (p) {
          if (head) { c.fillStyle = '#7A6E60'; c.font = '20px Pitch, "Courier New", monospace'; c.fillText(plName(p.pl, p.i).toUpperCase(), x, pad + 22); }
          c.drawImage(p.canvas, x, top);
          x += p.canvas.width + gap;
        });
        var y = top + H + 52;
        c.fillStyle = '#1A1A1A'; c.font = '30px "Libre Caslon Text", Georgia, serif';
        c.fillText(((o.meta && o.meta.title) || prod.handle) + '  ·  ' + (METAL_LABEL[m] || m), pad, y);
        c.fillStyle = '#7A6E60'; c.font = '22px Pitch, "Courier New", monospace';
        dl.forEach(function (l, k) { c.fillText(l, pad, y + 42 + k * 32); });
        c.fillText('Approved at add to cart  ·  ' + new Date().toLocaleString(), pad, y + 42 + dl.length * 32 + 12);
        return cv.toDataURL('image/jpeg', 0.88);
      });
    }
    /* every face where the customer sent artwork, uploaded once so the order can point at the file */
    function uploadArtwork() {
      if (!o.api) return Promise.resolve(null);
      var jobs = [];
      chosen().forEach(function (i) {
        var j = st.per[i].style, v = sv(i, j), s = sst(i, j);
        if (v.mode !== 'upload' || !s.art || s.artUrl) return;
        jobs.push(fetch(String(o.api).replace(/\/$/, '') + '/api/proof', {
          method: 'POST', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ image: s.art, meta: {
            handle: (o.meta && o.meta.handle) || prod.handle, productId: o.meta && o.meta.productId,
            title: ((o.meta && o.meta.title) || '') + ' — artwork for ' + plName(P[i], i), spec: { kind: 'artwork', file: s.artName } } })
        }).then(function (r) { return r.json(); }).then(function (jr) { if (jr && jr.ok) s.artUrl = jr.url; }).catch(function () {}));
      });
      return Promise.all(jobs);
    }
    function uploadProof(dataUrl) {
      if (!o.api) return Promise.resolve(null);
      var v = o.getVariant ? o.getVariant() : null;
      var body = JSON.stringify({ image: dataUrl, meta: {
        handle: (o.meta && o.meta.handle) || prod.handle, productId: o.meta && o.meta.productId, title: o.meta && o.meta.title,
        variantId: v && v.id, variant: v && v.title, spec: spec() } });
      /* Retry. One transient failure here used to cost the piece its proof permanently: the order
         still goes through (by design), but nothing records that the proof was lost — that is how
         order #1029 shipped without one on 2026-09-17. The 10s cap in attachForm still bounds the
         whole attempt, so the backoff is deliberately short. */
      function attempt(n) {
        return fetch(String(o.api).replace(/\/$/, '') + '/api/proof', {
          method: 'POST', headers: { 'content-type': 'application/json' }, body: body
        }).then(function (r) { return r.json(); }).then(function (j) {
          if (j && j.ok && j.url) return j.url;
          throw new Error((j && j.error) || 'proof upload refused');
        }).catch(function (e) {
          if (n >= 3) { try { console.warn('[coeura] proof upload failed after ' + n + ' tries', e); } catch (_) {} return null; }
          return new Promise(function (res) { setTimeout(res, 300 * n); }).then(function () { return attempt(n + 1); });
        });
      }
      return attempt(1);
    }
    /* take over the product form's submit: write the properties, compose + upload the proof, then submit.
       A failed or slow upload never blocks the order (10s cap); the text properties always go through. */
    function attachForm(form, btn) {
      var box = form.querySelector('[data-props]');
      if (!box) { box = document.createElement('div'); box.hidden = true; box.setAttribute('data-props', ''); form.appendChild(box); }
      function put(name, val) {
        var inp = document.createElement('input');
        inp.type = 'hidden'; inp.name = 'properties[' + name + ']'; inp.value = val;
        box.appendChild(inp);
      }
      form.addEventListener('submit', function (e) {
        if (form.__cgGo) return;
        // Nothing composed: this is the plain piece. Carry no engraving properties and let the add
        // through untouched (pdp.js's plain handler, or the native submit, takes it from here).
        if (!startedAny()) { box.innerHTML = ''; return; }
        e.preventDefault();
        if (!status().ready) return;
        if (btn) { btn.disabled = true; btn.textContent = 'Setting your proof'; }
        box.innerHTML = '';
        properties().forEach(function (p) { put(p[0], p[1]); });
        var done = false;
        function go(url) {
          if (done) return;
          done = true;
          if (url) put('_Proof', url);
          var many = chosen().length > 1;
          chosen().forEach(function (i) {                       // the customer's own artwork, as a file the atelier can open
            var j = st.per[i].style, v = sv(i, j), s = sst(i, j);
            if (v.mode === 'upload' && s.artUrl) put('_Artwork' + (many ? ' ' + plName(P[i], i) : ''), s.artUrl);
          });
          var eng = box.querySelector('input[name="properties[_Engraving]"]');
          if (eng) eng.value = JSON.stringify(spec());        // now that any artwork has a URL of its own
          put('_Approved', new Date().toISOString());
          // A host (pdp.js) may take over the final add — AJAX add + cart drawer / upsell / edit-replace —
          // instead of the default full-page submit. It gets the composed form (properties written) and
          // the spec; on any failure it must submit(form) itself. Keep this hook when editing this file.
          if (o.onSubmit) { o.onSubmit(form, spec(), url); return; }
          form.__cgGo = true;
          HTMLFormElement.prototype.submit.call(form);
        }
        var timer = setTimeout(function () { go(null); }, 10000);
        uploadArtwork()
          .then(proofImage)
          .then(function (d) { return d ? uploadProof(d) : null; })
          .then(function (u) { clearTimeout(timer); go(u); }, function () { clearTimeout(timer); go(null); });
      });
      window.addEventListener('pageshow', function () { form.__cgGo = false; if (o.onChange) o.onChange(); });
    }

    /* ---- hand-off from Begin a Piece: ?letters= ?night= ?words= ?lines=["",""] ?font= ?place= ?style= ?sign= ---- */
    function prefill(q) {
      var place = q.get('place');
      if (place) P.forEach(function (pl, i) { if (pl.id === place) st.active = i; });
      if (st.active < 0) st.active = 0;   // arriving from Begin with a composition always lands on a face
      var wantStyle = q.get('style');
      P.forEach(function (pl, i) {
        if (wantStyle) {
          stylesOf(pl).forEach(function (s, j) {
            var v = styleView(pl, j);
            if (s.id === wantStyle || v.preset === wantStyle) st.per[i].style = j;
          });
        }
        var j = st.per[i].style, v = sv(i, j), s = sst(i, j), box = sbox(i, j);
        if (!box) return;
        if ((v.mode === 'initials' || v.mode === 'monogram') && q.get('letters')) {
          var max = v.mode === 'monogram' ? v.monogram.count : 2;
          s.letters = q.get('letters').replace(/[^a-zA-Z]/g, '').slice(0, max);
          var li = box.querySelector('[data-letters]'); if (li) li.value = s.letters.toUpperCase();
        }
        if (v.mode === 'date' && q.get('night')) {
          s.night = q.get('night');
          var ni = box.querySelector('[data-night]'); if (ni) ni.value = s.night;
          moonSentence(box, s.night);
          skyCtls.forEach(function (c) { c.fill(s.night); });   // the same carry as typing the night in
        }
        if (v.mode === 'engraving' && i === st.active) {
          var lines = null;
          try { lines = JSON.parse(q.get('lines') || 'null'); } catch (e) {}
          if (Array.isArray(lines)) lines.slice(0, s.lines.length).forEach(function (t, k) { if (v.text.lines[k].kind !== 'glyph') { s.lines[k].text = stripGlyphs(t); flow(v, s, k); } });
          else if (q.get('words')) { s.lines[0].text = stripGlyphs(q.get('words')); flow(v, s, 0); }
          var sign = glyphBy(q.get('sign') || '');
          if (sign) v.text.lines.forEach(function (cl, k) { if (cl.kind === 'glyph') { s.lines[k].text = sign.char; var selEl = box.querySelector('[data-sign="' + k + '"]'); if (selEl) selEl.value = sign.id; } });
          syncLineInputs(i, j);
          var f = q.get('font');
          if (f && v.text.fonts.indexOf(f) !== -1) {
            s.font = f;
            box.querySelectorAll('[data-font]').forEach(function (x) { x.classList.toggle('is-on', x.getAttribute('data-font') === f); });
          }
        }
      });
      setActive(st.active);
    }

    build();
    var keys = [];
    function addKey(f) { if (f && keys.indexOf(f) === -1) keys.push(f); }
    P.forEach(function (pl) {
      stylesOf(pl).forEach(function (_, j) {
        var v = styleView(pl, j);
        if (v.monogram) addKey(v.monogram.font);
        if (!v.text) return;
        v.text.fonts.forEach(addKey);
        v.text.lines.forEach(function (l) { addKey(l.font); });
      });
    });
    if (keys.length && R().loadFonts) R().loadFonts(keys).then(function () { changed(); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { changed(); });
    /* interlaced initials: fetch all three Sappho layers now, for the page and for the ink pass, so the
       second letter (the only one drawn in Sappho One) doesn't wait on a download before it can show */
    var hasInitials = P.some(function (pl) { return stylesOf(pl).some(function (_, j) { return styleView(pl, j).mode === 'initials'; }); });
    if (hasInitials) {
      var SAPPHO = ['Sappho', 'Sappho One', 'Sappho Two'];
      monoFontsLoad(SAPPHO);
      SAPPHO.forEach(function (f) { if (R().FONT_FILES[f]) fontDataURL(R().FONT_FILES[f]); });
    }
    return {
      render: render, status: status, properties: properties, spec: spec, proofImage: proofImage,
      attachForm: attachForm, prefill: prefill, setActive: setActive, setStyle: setStyle, changed: changed, layout: layFor,
      started: startedAny,
      state: st, product: prod, destroy: function () { dead = true; }
    };
  }

  window.CoeuraEngrave = {
    normalize: normalize, merge: merge, productFor: productFor, load: load, fillText: fillText, fillMono: fillMono,
    plateKey: plateKey, stageFor: stageFor, resolveSrc: resolveSrc, Composer: Composer,
    GLYPHS: GLYPHS, glyphBy: glyphBy, PRESETS: PRESETS, presetById: presetById, applyPreset: applyPreset,
    stylesOf: stylesOf, styleView: styleView, fillStyle: fillStyle, LINES_FAMILY: LINES_FAMILY, isLines: isLines, stripGlyphs: stripGlyphs,
    METAL_TOKEN: METAL_TOKEN, METAL_LABEL: METAL_LABEL, TONE_LABEL: TONE_LABEL, clone: clone, esc: esc
  };
})();
